import React, {
  useState,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
  useRef,
  useContext,
} from "react";

import { NormalText } from "components/index";
import { SessionContext } from "contexts";
import { TSubmitAdvertiseFormRequest } from "api/clinicos";
import { generateUUID } from "utils/generateUUID";
import { FormNameType, UploadTypeType } from "api/type";
import { useUserLocation } from "utils/customHook";
import { submitAdvertiseForm } from "api/clinicos";

import {
  postOpenAIChat,
  TPostOpenAIChatRequest,
  TPostOpenAIChatResponse,
  getBeforeImageUrl,
} from "api/clinicos";
import Markdown from "react-markdown";
import mixpanel from "mixpanel-browser";

type ScreenChatBotProps = {};

type Message = {
  sender: "user" | "bot";
  content: string;
  displayedContent?: string;
  image_url?: string | null;
};

const ScreenChatBot: React.FC<ScreenChatBotProps> = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState<string>("");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]);
  const [history, setHistory] = useState<
    { prompt: string; response: string }[]
  >([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [uploadedImage, setUploadedImage] = useState<Blob | null>(null);
  const session = useContext(SessionContext);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isConsultationFormVisible, setIsConsultationFormVisible] =
    useState<boolean>(false);
  const [isTextSubmittedAfterForm, setIsTextSubmittedAfterForm] =
    useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [tel, setTel] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);
  const fetchUserLocation = useUserLocation(session);
  const [questionCount, setQuestionCount] = useState<number>(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      //To do: Extension checks and size
      // const allowedExtensions = /(\.png|\.jpeg|\.jpg)$/i;
      const blob = new Blob([file], { type: file.type });
      setUploadedImage(blob);
      setPreviewImage(URL.createObjectURL(file));
      event.target.value = "";
    }
  };
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (
      lastMessage &&
      lastMessage.sender === "bot" &&
      lastMessage.displayedContent !== lastMessage.content
    ) {
      let i = lastMessage.displayedContent?.length || 0;
      const typingInterval = setInterval(() => {
        if (i < lastMessage.content.length) {
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            const currentMessage = updatedMessages[updatedMessages.length - 1];
            currentMessage.displayedContent = currentMessage.content.substring(
              0,
              i + 1,
            );
            return updatedMessages;
          });
          i++;
        } else {
          clearInterval(typingInterval);
        }
      }, 10);

      return () => clearInterval(typingInterval);
    }
  }, [messages]);

  const handleOnClickMessage = async (message: string) => {
    if (!session) {
      console.error("Session is null");
      return;
    }
    if (message === "Schedule a Consultation") {
      setFirstName("");
      setLastName("");
      setEmail("");
      setTel("");
      setFormError(null);
      setIsConsultationFormVisible(true);
      setIsTextSubmittedAfterForm(false);
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      return;
    }

    setIsConsultationFormVisible(false);
    setIsTextSubmittedAfterForm(true);
    let image_url: string | null = previewImage;

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        sender: "user",
        content: message,
        image_url: image_url,
      },
    ]);
    setFollowUpQuestions([]);
    setUserInput("");
    setPreviewImage(null);
    setIsTyping(true);

    setQuestionCount((prevCount) => prevCount + 1);

    try {
      if (uploadedImage) {
        const uploadedImageUrl = await getBeforeImageUrl(
          session,
          uploadedImage,
        );
        image_url = uploadedImageUrl;

        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          const lastMessageIndex = updatedMessages.length - 1;
          updatedMessages[lastMessageIndex].image_url = uploadedImageUrl;
          return updatedMessages;
        });
      }
      const body: TPostOpenAIChatRequest = {
        content: message,
        temperature: 0.5,
        max_tokens: 500,
        history: history.slice(-2),
        image_url: image_url,
      };
      console.log("Sending request to backend:", body);

      const response: TPostOpenAIChatResponse = await postOpenAIChat(
        session,
        body,
      );

      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "bot", content: response.response, displayedContent: "" },
      ]);
      setHistory((prevHistory) => [
        ...prevHistory,
        { prompt: message, response: response.response },
      ]);
      const questionsArray = response.questions[0]
        .split("', '")
        .map((q) => q.replace(/['"]+/g, "").trim());
      if (questionCount >= 1) {
        questionsArray.push("Schedule a Consultation");
      }
      setFollowUpQuestions(questionsArray);
    } catch (error: any) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: "Sorry, something went wrong.",
        },
      ]);
    } finally {
      setIsTyping(false);
      setUploadedImage(null);
      mixpanel.track("Chatbot Message Sent", {
        propertyName: "Message",
        propertyValue: message,
      });
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUserInput(event.target.value);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && userInput.trim() !== "") {
      handleOnClickMessage(userInput);
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setPreviewImage(null);
  };

  const validateForm = () => {
    if (!firstName || !lastName || !email) {
      setFormError(
        "Please fill all required fields: First Name, Last Name, and Email.",
      );
      return false;
    }
    setFormError(null);
    return true;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsConsultationFormVisible(false);
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        sender: "bot",
        content:
          "Thank you for filling the form, we will reach out to you at the earliest.",
      },
    ]);

    const body: TSubmitAdvertiseFormRequest = {
      form_user_id: generateUUID(),
      form_name: FormNameType.ONBOARDING_CHATBOT,
      is_debug: process.env.REACT_APP_ENV === "production" ? false : true,
      form_data: { name: firstName, phone: tel },
      patient_record: {
        first_name: firstName,
        last_name: lastName,
        phone: tel,
        email: email,
        location: await fetchUserLocation(),
        upload_type: UploadTypeType.CHATBOT,
      },
    };

    if (!session) {
      return;
    }

    try {
      await submitAdvertiseForm(session, body);
      setIsTextSubmittedAfterForm(false);
    } catch (error) {
      console.error("Error submitting the form:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content:
            "There was an issue submitting the form. Please try again later.",
        },
      ]);
    }
  };

  return (
    <div className="screen-container" style={{ overflow: "visible" }}>
      <img
        style={{ width: "138px", margin: "0 auto" }}
        src="https://www.clinic-assets.clinic-os.com/drfrank-clinic/clinic-logo.png"
        alt=""
      />
      <div
        className="remove_scroll_bar"
        style={{
          flex: 1,
          overflowY: "auto",
          borderRadius: "5px",
          padding: "0px 10px",
          marginBottom: "5px",
        }}
      >
        {messages.length === 0 && (
          <>
            <NormalText
              style={{
                color: "#515561",
                fontWeight: 600,
                fontSize: "28px",
                textAlign: "center",
                margin: "30px 0 10px 0",
              }}
              textContent={"Capabilities"}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                background: "#DDDEE0",
                padding: "10px",
                borderRadius: "10px",
                gap: "5px",
                marginBottom: "15px",
              }}
            >
              <NormalText textContent={"Answer questions about dermatology"} />
              <NormalText textContent={"(Ask me anything you like!)"} />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                background: "#DDDEE0",
                padding: "10px",
                borderRadius: "10px",
                gap: "5px",
              }}
            >
              <NormalText
                textContent={"Tell you more about the services I offer"}
              />
              <NormalText
                textContent={"(Signature treatments, fat removal, & more)"}
              />
            </div>
          </>
        )}
        {messages.map((msg, index) => (
          <div key={index} style={{ margin: "10px 0" }}>
            <div
              style={{
                display: "flex",
                padding: "10px",
                borderRadius: "5px",
                background: msg.sender === "user" ? "#DDDEE0" : "#F3F3F3",
                width: "fit-content",
                maxWidth: "85%",
                marginLeft: msg.sender === "user" ? "auto" : "0",
                flexDirection: "column",
              }}
            >
              {msg.image_url && (
                <img
                  src={msg.image_url}
                  alt="uploaded"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    marginBottom: "10px",
                  }}
                />
              )}
              <Markdown>{msg.displayedContent || msg.content}</Markdown>
            </div>
          </div>
        ))}
        {isConsultationFormVisible && (
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "20px",
              borderRadius: "10px",
              marginBottom: "20px",
              maxWidth: "400px",
              margin: "0 auto",
            }}
          >
            <h3
              style={{
                textAlign: "center",
                marginBottom: "20px",
                color: "#333",
              }}
            >
              Schedule a Consultation
            </h3>
            <div style={{ marginBottom: "10px" }}>
              <label style={{ fontSize: "14px", fontWeight: "bold" }}>
                First Name*:
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                style={{
                  width: "90%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  marginBottom: "10px",
                }}
                required
              />
            </div>
            <div style={{ marginBottom: "10px" }}>
              <label style={{ fontSize: "14px", fontWeight: "bold" }}>
                Last Name*:
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                style={{
                  width: "90%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  marginBottom: "10px",
                }}
                required
              />
            </div>
            <div style={{ marginBottom: "10px" }}>
              <label style={{ fontSize: "14px", fontWeight: "bold" }}>
                Email*:
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                style={{
                  width: "90%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  marginBottom: "10px",
                }}
                required
              />
            </div>
            <div style={{ marginBottom: "10px" }}>
              <label style={{ fontSize: "14px", fontWeight: "bold" }}>
                Mobile Number:
              </label>
              <input
                type="tel"
                value={tel}
                onChange={(e) => setTel(e.target.value)}
                placeholder="Mobile Number"
                style={{
                  width: "90%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  marginBottom: "10px",
                }}
              />
            </div>
            {formError && (
              <div style={{ color: "red", marginBottom: "10px" }}>
                {formError}
              </div>
            )}
            <button
              style={{
                backgroundColor: "#007bff",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                width: "100%",
                border: "none",
              }}
              onClick={handleFormSubmit}
            >
              Submit
            </button>
            {/* <PrimaryButton onClick={function (): void {
              throw new Error("Function not implemented.");
            } } text={"Submit"} fullWidth displayPoweredBy={false}/> */}
          </div>
        )}
        {!isConsultationFormVisible &&
          isTextSubmittedAfterForm &&
          followUpQuestions.length > 0 && (
            <div
              style={{
                marginTop: "20px",
                padding: "10px",
                background: "rgb(235, 205, 193)",
                borderRadius: "10px",
              }}
            >
              <div
                style={{
                  marginBottom: "10px",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                Related
              </div>
              {followUpQuestions.map((question, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: "10px",
                    borderTop: "1px solid rgb(0, 0, 0)",
                  }}
                >
                  <button
                    onClick={() => handleOnClickMessage(question)}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "10px 20px 10px 10px",
                      textAlign: "left",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      position: "relative",
                      fontSize: "16px",
                    }}
                  >
                    {question}
                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "10px",
                        fontSize: "24px",
                      }}
                    >
                      {"+"}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}
        {isTyping && (
          <div
            style={{
              textAlign: "left",
              borderRadius: "5px",
              background: "#eeeeee",
              display: "inline-block",
              padding: "10px",
            }}
          >
            <em>Dr Frank is responding...</em>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#000",
          borderRadius: "5px",
          padding: "12px 12px",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        <label
          htmlFor="file-input"
          style={{
            cursor: "pointer",
            position: "relative",
            display: "flex",
            alignItems: "center",
            marginRight: "10px",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_7741_7527"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="24"
              height="24"
            >
              <rect width="24" height="24" fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask0_7741_7527)">
              <path
                d="M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H13V5H5V19H19V11H21V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM6 17H18L14.25 12L11.25 16L9 13L6 17ZM17 9V7H15V5H17V3H19V5H21V7H19V9H17Z"
                fill="white"
              />
            </g>
          </svg>
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          id="file-input"
          style={{ display: "none" }}
        />
        {previewImage && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "10px",
              position: "relative",
            }}
          >
            <img
              src={previewImage}
              alt="Preview"
              style={{
                width: "30px",
                height: "30px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />

            <button
              onClick={handleRemoveImage}
              style={{
                position: "absolute",
                top: "-5px",
                right: "-5px",
                backgroundColor: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "16px",
                height: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: "0",
              }}
            >
              ✖
            </button>
          </div>
        )}
        <input
          style={{
            flex: 1,
            fontSize: "16px",
            fontWeight: 600,
            padding: "4px 5px",
            borderRadius: "5px",
            boxSizing: "border-box",
            outline: "none",
            background: "#000",
            color: "#fff",
            border: "none",
            // marginLeft: "20px",
            textAlign: "left",
          }}
          value={userInput}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="Ask Dr Frank Anything"
          // disabled={isTyping}
        />
      </div>
    </div>
  );
};

export default ScreenChatBot;
